import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '@/App';

const AdminCustomForms = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [selectedFormName, setSelectedFormName] = useState('');

  // Upload form state
  const [formName, setFormName] = useState('');
  const [state, setState] = useState('');
  const [category, setCategory] = useState('tax');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchSubmissions();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axiosInstance.get('/admin/custom-forms');
      setForms(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching forms:', error);
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axiosInstance.get('/admin/form-submissions');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const viewSubmissions = (formId, formName) => {
    setSelectedFormName(formName);
    const formSubs = submissions.filter(s => s.formId === formId);
    setSubmissions(formSubs.length > 0 ? formSubs : []);
    setShowSubmissions(true);
  };

  const downloadPdf = (pdfUrl) => {
    if (pdfUrl) {
      window.open(`${API}${pdfUrl}`, '_blank');
    } else {
      alert('PDF not generated. The worker may need to re-submit the form.');
    }
  };

  const regeneratePdf = async (submissionId) => {
    if (!window.confirm('Regenerate PDF for this submission?')) return;
    
    try {
      const response = await axiosInstance.post(`/admin/form-submissions/${submissionId}/regenerate-pdf`);
      alert('✓ PDF regenerated successfully!');
      
      // Refresh submissions
      fetchSubmissions();
      
      // Open the new PDF
      if (response.data.filledPdfUrl) {
        window.open(`${API}${response.data.filledPdfUrl}`, '_blank');
      }
    } catch (error) {
      console.error('Error regenerating PDF:', error);
      alert('Error regenerating PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('Please select a PDF file');
      return;
    }

    if (!formName) {
      alert('Please enter a form name');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('formName', formName);
      formData.append('state', state);
      formData.append('category', category);
      formData.append('isRequired', 'true');  // Always required - if admin uploads it, workers need to fill it
      formData.append('file', selectedFile);

      console.log('Uploading form:', formName, selectedFile.name);

      const response = await axiosInstance.post(
        '/admin/custom-forms',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Upload response:', response.data);
      
      const fieldsDetected = response.data.autoDetectedFields || 0;
      const isFillable = response.data.isFillablePdf;
      const serverMessage = response.data.message || '';
      
      // Show appropriate message based on PDF type
      if (!isFillable) {
        alert(`⚠️ Non-Fillable PDF Detected\n\nThis PDF doesn't have built-in form fields. ${fieldsDetected} fields were auto-detected, but positions may not be accurate.\n\nYou'll be redirected to the Field Mapper to manually position fields correctly.`);
      } else if (fieldsDetected > 0) {
        alert(`✓ Form uploaded successfully!\n\n${fieldsDetected} fillable fields detected.\n\nRedirecting to review the field positions...`);
      } else {
        alert(`Form uploaded successfully.\n\nNo fields could be auto-detected from this PDF. You'll be redirected to manually place fields where workers need to fill in data.`);
      }
      
      setShowUploadModal(false);
      resetUploadForm();
      
      // Refresh forms list
      await fetchForms();

      // Always navigate to mapping page for review/adjustment
      // Use replace to avoid building up navigation history
      setTimeout(() => {
        navigate(`/admin/custom-forms/${response.data.formId}/map`, { replace: true });
      }, 500);
    } catch (error) {
      console.error('Error uploading form:', error);
      console.error('Error details:', error.response?.data);
      alert('Error uploading form: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setFormName('');
    setState('');
    setCategory('tax');
    setSelectedFile(null);
  };

  const handleDeleteForm = async (formId, formName) => {
    if (!window.confirm(`Are you sure you want to delete "${formName}"?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/custom-forms/${formId}`);
      alert('Form deleted successfully');
      fetchForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      alert('Error deleting form: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      tax: 'bg-blue-100 text-blue-800',
      employment: 'bg-green-100 text-green-800',
      safety: 'bg-orange-100 text-orange-800',
      benefits: 'bg-purple-100 text-purple-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading forms...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">PDF Form Builder</h1>
              <p className="text-gray-600 mt-1">Create, upload, and manage custom PDF forms for workers to complete</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/custom-forms/create')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Form
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
            >
              + Upload PDF
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-2xl">📝</div>
            <div>
              <h3 className="font-semibold text-blue-900">Two Ways to Create Forms</h3>
              <p className="text-sm text-blue-800 mt-1">
                <strong>Create Form:</strong> Build a form from scratch with drag-and-drop fields.<br/>
                <strong>Upload PDF:</strong> Upload an existing PDF and our AI will detect form fields automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Forms List */}
        {forms.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No forms created yet</h3>
            <p className="text-gray-500 mb-4">Create your first form or upload an existing PDF</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/admin/custom-forms/create')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Create Form
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
              >
                Upload PDF
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((form) => {
              const formSubmissionCount = submissions.filter(s => s.formId === form.id).length;
              const needsReview = form.isFillablePdf === false || !form.mappingComplete;
              const isCreatedForm = form.isCreatedForm === true;
              return (
              <div key={form.id} className={`bg-white rounded-lg shadow hover:shadow-lg transition p-6 ${needsReview && !isCreatedForm ? 'border-2 border-yellow-400' : ''}`}>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex-1">{form.formName}</h3>
                  <div className="flex gap-1">
                    {isCreatedForm && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Created</span>
                    )}
                    {form.isFillablePdf === false && !isCreatedForm && (
                      <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Non-Fillable</span>
                    )}
                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                  </div>
                </div>

                {needsReview && (
                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    ⚠️ Review field positions in Field Mapper
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">State:</span>
                    <span>{form.state || 'Federal'}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(form.category)}`}>
                      {form.category}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">Fields:</span>
                    <span>{form.fields?.length || 0} detected</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium mr-2">Submissions:</span>
                    <span className={formSubmissionCount > 0 ? 'text-green-600 font-semibold' : ''}>{formSubmissionCount}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-4 border-t">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/custom-forms/${form.id}/map`)}
                      className="flex-1 px-3 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition"
                    >
                      {form.mappingComplete ? 'Edit Fields' : 'Review Fields'}
                    </button>
                    <button
                      onClick={() => handleDeleteForm(form.id, form.formName)}
                      className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
                    >
                      🗑
                    </button>
                  </div>
                  {formSubmissionCount > 0 && (
                    <button
                      onClick={() => viewSubmissions(form.id, form.formName)}
                      className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                    >
                      📋 View {formSubmissionCount} Submission{formSubmissionCount > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            )})}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Upload New Form</h2>
            
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Name *
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., California DE 4"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="e.g., CA, TX, OH (leave empty for federal)"
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                >
                  <option value="tax">Tax Forms</option>
                  <option value="employment">Employment</option>
                  <option value="safety">Safety</option>
                  <option value="benefits">Benefits</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PDF File *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">Selected: {selectedFile.name}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUploadForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload & Map Fields'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {showSubmissions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                📋 Submissions for {selectedFormName}
              </h2>
              <button
                onClick={() => {
                  setShowSubmissions(false);
                  fetchSubmissions(); // Refresh all submissions
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {submissions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No submissions yet</p>
            ) : (
              <div className="space-y-4">
                {submissions.map((sub) => (
                  <div key={sub.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{sub.workerName}</h3>
                        <p className="text-sm text-gray-500">
                          Submitted: {new Date(sub.submittedAt).toLocaleDateString()} at {new Date(sub.submittedAt).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {sub.filledPdfUrl ? (
                          <button
                            onClick={() => downloadPdf(sub.filledPdfUrl)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            📥 Download PDF
                          </button>
                        ) : (
                          <button
                            onClick={() => regeneratePdf(sub.id)}
                            className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                          >
                            🔄 Generate PDF
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Show some key data */}
                    <div className="mt-3 pt-3 border-t text-sm text-gray-600">
                      <p className="font-medium mb-1">Form Data:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(sub.data || {}).slice(0, 6).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-gray-400">{key.replace(/_/g, ' ')}:</span>{' '}
                            <span>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value).substring(0, 30)}</span>
                          </div>
                        ))}
                      </div>
                      {Object.keys(sub.data || {}).length > 6 && (
                        <p className="text-gray-400 mt-1">...and {Object.keys(sub.data).length - 6} more fields</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setShowSubmissions(false);
                  fetchSubmissions();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomForms;
